/**
 * ローカルファイルインポートAPI
 *
 * ファイル/ディレクトリをアップロードし、各ドメインへ Products/Episodes として登録する。
 * 進捗は SSE (text/event-stream) で受信する。
 */

import { apiClient } from '@/api/client'

export type ImportTag = { name: string; type: string }

/**
 * axios の onDownloadProgress で受信する累積テキストから SSE の `data:` 行を逐次パースする。
 * チャンク境界で分割された不完全な行はバッファに保持し、次回以降に持ち越す。
 */
const createSseConsumer = (onMessage: (event: ImportEvent) => void) => {
  let buffer = ''
  let processedLength = 0
  return (responseText: string | undefined): void => {
    if (!responseText) return
    buffer += responseText.slice(processedLength)
    processedLength = responseText.length
    const lines = buffer.split('\n')
    // 末尾の要素は改行で終わっていない可能性があるためバッファに残す
    buffer = lines.pop() ?? ''
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue
      try {
        onMessage(JSON.parse(line.slice(6)) as ImportEvent)
      } catch {
        // パースエラーは無視
      }
    }
  }
}

export type ImportEvent =
  | { status: 'start'; total: number; domain_product_id: string; product_id: number | null }
  | { status: 'file_done'; file: string; index: number; total: number }
  | { status: 'file_skipped'; file: string }
  | { status: 'file_error'; file: string; message: string }
  | {
      status: 'done'
      domain_product_id: string
      product_id: number | null
      registered: number
      skipped: number
      failed: number
    }
  | { status: 'error'; message: string }

export type ImportUploadParams = {
  files: File[]
  domain: string
  title: string
  tags: ImportTag[]
  mode: 'new' | 'append'
  targetDomainProductId?: string
  thumbnail?: File | null
  thumbnailUrl?: string | null
  releaseDate?: string | null
  description?: string | null
  /**
   * 図書館予約 (Calil) 由来のインポート時に渡す正規化済み ISBN。
   * バックエンドは new モードで `domain_product_id = ISBN` として登録する。
   */
  isbn?: string | null
}

/**
 * ファイルをアップロードしインポートする（SSE進捗付き）。
 *
 * Cloudflare Free プランのリクエストボディ上限 (100MB) を回避するため、
 * ファイルを ~50MB のチャンクに分割してアップロードした後、
 * `/import/chunk/complete` へメタデータを送って本処理を実行する。
 *
 * サムネイル画像は通常小さいためチャンク化せず、`complete` に multipart で
 * 直接付与する。
 */
export const streamImportUpload = async (
  params: ImportUploadParams,
  onMessage: (event: ImportEvent) => void,
  onError: (error: Error) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const uploadedIds: string[] = []
  const abortSession = async (): Promise<void> => {
    // best-effort でサーバ側の一時ファイルを削除する
    // (Promise.allSettled が rejection を吸収するため .catch は不要)
    await Promise.allSettled(uploadedIds.map((id) => apiClient.delete(`/import/chunk/${id}`)))
  }

  try {
    // 1. 各ファイルをチャンク分割してアップロード
    const uploadedFiles: { upload_id: string; filename: string }[] = []
    for (const file of params.files) {
      if (signal?.aborted) throw new DOMException('aborted', 'AbortError')
      const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name
      const filename = relativePath.split('/').pop() || file.name
      const uploadId = await uploadFileInChunks(file, filename, uploadedIds, signal)
      uploadedFiles.push({ upload_id: uploadId, filename })
    }

    // 2. メタデータを FormData で送信し SSE を購読
    const form = new FormData()
    form.append('uploaded_files', JSON.stringify(uploadedFiles))
    form.append('domain', params.domain)
    form.append('title', params.title)
    form.append('tags', JSON.stringify(params.tags))
    form.append('mode', params.mode)
    if (params.targetDomainProductId) {
      form.append('target_domain_product_id', params.targetDomainProductId)
    }
    if (params.thumbnail) {
      form.append('thumbnail', params.thumbnail)
    }
    if (params.thumbnailUrl) {
      form.append('thumbnail_url', params.thumbnailUrl)
    }
    if (params.releaseDate) {
      form.append('release_date', params.releaseDate)
    }
    if (params.description) {
      form.append('description', params.description)
    }
    if (params.isbn) {
      form.append('isbn', params.isbn)
    }

    const consume = createSseConsumer(onMessage)
    await apiClient.post('/import/chunk/complete', form, {
      responseType: 'text',
      signal,
      headers: { Accept: 'text/event-stream' },
      onDownloadProgress: (progressEvent) => {
        consume(progressEvent.event?.target?.response as string | undefined)
      },
    })
  } catch (error) {
    if (signal?.aborted) {
      await abortSession()
      return
    }
    await abortSession()
    onError(error instanceof Error ? error : new Error('Unknown error'))
  }
}

/**
 * 1 ファイルをチャンク分割してアップロードし upload_id を返す。
 * チャンクは順番にアップロードする (BE 側で順序を厳密に検証)。
 * init 後に uploadedIds へ即座に登録し、チャンク PUT 途中での abort でも
 * サーバ側の一時ファイルが確実に回収されるようにする。
 */
const CHUNK_UPLOAD_SIZE = 50 * 1024 * 1024 // 50MB

const uploadFileInChunks = async (
  file: File,
  filename: string,
  uploadedIds: string[],
  signal?: AbortSignal,
): Promise<string> => {
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_UPLOAD_SIZE))
  const initRes = await apiClient.post<{ upload_id: string }>(
    '/import/chunk/init',
    {
      filename,
      total_size: file.size,
      total_chunks: totalChunks,
    },
    { signal },
  )
  const uploadId = initRes.data.upload_id
  // init 直後に登録して、以降の PUT 失敗・abort でも確実に DELETE 対象に含める
  uploadedIds.push(uploadId)

  for (let index = 0; index < totalChunks; index += 1) {
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError')
    const start = index * CHUNK_UPLOAD_SIZE
    const end = Math.min(start + CHUNK_UPLOAD_SIZE, file.size)
    const chunk = file.slice(start, end)
    // 空ファイル (size=0) は slice が空 Blob になるので送信不可 → 事前にガード
    if (chunk.size === 0) {
      throw new Error(`chunk ${index} is empty for ${filename}`)
    }
    await apiClient.put(`/import/chunk/${uploadId}`, chunk, {
      signal,
      params: { index },
      headers: { 'Content-Type': 'application/octet-stream' },
    })
  }
  return uploadId
}

export type CsvImportRow = {
  path: string
  customName: string | null
  uploadDate: string | null
}

export type CsvImportParams = {
  domain: string
  title: string
  tags: ImportTag[]
  mode: 'new' | 'append'
  targetDomainProductId?: string
  description?: string | null
  rows: CsvImportRow[]
}

/**
 * CSVで指定したローカルパス群を順番にインポートする（SSE進捗付き）。
 * 2列目をEpisodeのカスタム名、3列目（YYYY-MM-DD）をupload_dateとして登録する。
 */
export const streamImportCsv = async (
  params: CsvImportParams,
  onMessage: (event: ImportEvent) => void,
  onError: (error: Error) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const body = {
    domain: params.domain,
    title: params.title,
    tags: params.tags,
    mode: params.mode,
    target_domain_product_id: params.targetDomainProductId ?? null,
    description: params.description ?? null,
    rows: params.rows.map((row) => ({
      path: row.path,
      custom_name: row.customName,
      upload_date: row.uploadDate,
    })),
  }

  const consume = createSseConsumer(onMessage)
  try {
    await apiClient.post('/import/csv', body, {
      responseType: 'text',
      signal,
      headers: { Accept: 'text/event-stream' },
      onDownloadProgress: (progressEvent) => {
        consume(progressEvent.event?.target?.response as string | undefined)
      },
    })
  } catch (error) {
    if (signal?.aborted) return
    onError(error instanceof Error ? error : new Error('Unknown error'))
  }
}

export type ImportProductSearchResult = { domain_product_id: string; title: string }

/** append モード用の既存 Product 検索 */
export const searchImportProducts = async (domain: string, q: string): Promise<ImportProductSearchResult[]> => {
  const res = await apiClient.get<ImportProductSearchResult[]>('/import/products/search', {
    params: { domain, q },
  })
  return res.data
}

export type AllowedExtensionsResponse = { extensions: string[] | null }

/** ドメインが受け入れ可能な拡張子一覧（null=任意） */
export const getAllowedExtensions = async (domain: string): Promise<AllowedExtensionsResponse> => {
  const res = await apiClient.get<AllowedExtensionsResponse>('/import/allowed-extensions', {
    params: { domain },
  })
  return res.data
}

/** ドメインの既存タグ一覧（補完候補用） */
export const getImportTags = async (domain: string): Promise<ImportTag[]> => {
  const res = await apiClient.get<ImportTag[]>('/import/tags', { params: { domain } })
  return res.data
}

export type AmazonMetadataResponse = {
  title: string
  tags: ImportTag[]
  thumbnail_url: string | null
  release_date: string | null
  description: string | null
}

/** AmazonのURLからAIでメタデータとサムネイルURLを抽出する */
export const fetchAmazonMetadata = async (url: string): Promise<AmazonMetadataResponse> => {
  const res = await apiClient.post<AmazonMetadataResponse>('/import/amazon-metadata', { url })
  return res.data
}

export type AmazonSearchResponse = {
  search_url: string
  product_url: string | null
}

/** ファイル名（拡張子なし）でAmazon JPを検索し、一番上のヒット商品URLを取得する */
export const searchAmazonTopHit = async (keyword: string): Promise<AmazonSearchResponse> => {
  const res = await apiClient.post<AmazonSearchResponse>('/import/amazon-search', { keyword })
  return res.data
}

export type CheckImportedResponse = { imported: string[] }

/** 対象Productに既にインポート済みのファイル名一覧を取得する */
export const checkImported = async (
  domain: string,
  domainProductId: string,
  filenames: string[],
): Promise<string[]> => {
  const res = await apiClient.post<CheckImportedResponse>('/import/check-imported', {
    domain,
    domain_product_id: domainProductId,
    filenames,
  })
  return res.data.imported
}

/**
 * ISBN による既存 Product 存在チェック (local ドメイン)。
 *
 * 図書館予約由来のインポート画面で、選択した本が既に自炊済みかを判定する。
 * `exists=true` の場合は既に登録済みなのでインポートをブロックする。
 */
export type CheckIsbnResponse = {
  exists: boolean
  domain_product_id: string | null
  title: string | null
  first_episode_id: string | null
}

export const checkIsbnImported = async (isbn: string): Promise<CheckIsbnResponse> => {
  const res = await apiClient.get<CheckIsbnResponse>('/import/check-isbn', {
    params: { isbn },
  })
  return res.data
}
